import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';
import { selectPendingOrder, selectCurrentOrder, createOrderWithReceiptAsync, clearPendingOrder } from "../features/order/orderSlice";
import { resetCartAsync } from "../features/cart/cartSlice";
import { selectUserInfo } from "../features/user/userSlice";
import { useLanguage } from '../context/LanguageContext';
import { useCommonTranslations } from '../context/commonTranslations';
import {
  CloudArrowUpIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  PhotoIcon,
  ArrowLeftIcon,
  DocumentCheckIcon
} from '@heroicons/react/24/outline';
import MainLayout from '../components/MainLayout';

export default function ReceiptUpload() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  
  const pendingOrder = useSelector(selectPendingOrder);
  const currentOrder = useSelector(selectCurrentOrder);
  const user = useSelector(selectUserInfo);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { language } = useLanguage();
  const t = useCommonTranslations(language);

  // Navigate when order is successfully created
  if (currentOrder && currentOrder.id) {
    return <Navigate to={`/order-success/${currentOrder.id}`} replace={true} />;
  }

  // Redirect if no pending order
  if (!pendingOrder) {
    return <Navigate to="/checkout" replace={true} />;
  }

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError(t.receipt.selectImageError);
        return;
      }
      
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setError(t.receipt.fileSizeError);
        return;
      }
      
      setSelectedFile(file);
      setError(null);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError(t.receipt.selectReceiptError);
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('receipt', selectedFile);
      formData.append('orderData', JSON.stringify(pendingOrder));

      const response = await dispatch(createOrderWithReceiptAsync(formData)).unwrap();

      if (response?.id) {
        setUploading(false);
      } else {
        setError('Order placed but could not get order ID. Please check your orders.');
        setUploading(false);
      }
    } catch (err) {
      setError(err.message || t.receipt.uploadFailed);
      setUploading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const file = e.dataTransfer.files[0];
    if (file) {
      // Create a fake event to reuse handleFileSelect logic
      handleFileSelect({ target: { files: [file] } });
    }
  };

  return (
    <MainLayout>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 sm:py-12">
        <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <button
            onClick={() => navigate('/checkout')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            <span>{t.receipt.backToCheckout}</span>
          </button>

          {/* Main Card */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                <DocumentCheckIcon className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">{t.receipt.pageTitle}</h1>
                <p className="text-blue-100 mt-1">
                  {t.receipt.orderTotal}: €{pendingOrder.totalAmount}
                </p>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="bg-blue-50 p-4 border-b border-blue-100">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">{t.receipt.orderId}:</span>
              <span className="text-sm font-mono font-medium text-gray-900">
                {t.receipt.tempOrderId}
              </span>
            </div>
            <div className="flex justify-between items-center mt-2">
              <span className="text-sm text-gray-600">{t.receipt.items}:</span>
              <span className="text-sm font-medium text-gray-900">
                {pendingOrder.totalItems} {t.receipt.itemsCount}
              </span>
            </div>
          </div>

          {/* Upload Section */}
          <div className="p-6">
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t.receipt.instructionsTitle}
              </h3>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                  <li>{t.receipt.instruction1}</li>
                  <li>{t.receipt.instruction2}</li>
                  <li>{t.receipt.instruction3}</li>
                  <li>{t.receipt.instruction4}</li>
                </ol>
              </div>
            </div>

            {/* Drag & Drop Area */}
            <div
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 ${
                preview 
                  ? 'border-green-400 bg-green-50' 
                  : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
              }`}
            >
              {preview ? (
                <div className="space-y-4">
                  <img
                    src={preview}
                    alt="Receipt preview"
                    className="max-h-64 mx-auto rounded-lg shadow-md"
                  />
                  <p className="text-sm text-gray-600">
                    {selectedFile?.name}
                  </p>
                  <button
                    onClick={() => {
                      setSelectedFile(null);
                      setPreview(null);
                    }}
                    className="text-sm text-red-600 hover:text-red-700 font-medium"
                  >
                    {t.receipt.removeChooseAnother}
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                    <PhotoIcon className="w-8 h-8 text-gray-400" />
                  </div>
                  <div>
                    <p className="text-gray-700 font-medium">
                      {t.receipt.dragDrop}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      {t.receipt.orClickBrowse}
                    </p>
                  </div>
                  <p className="text-xs text-gray-400">
                    {t.receipt.supportedFormats}
                  </p>
                </div>
              )}
              
              <input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="mt-4 flex items-center gap-2 text-red-600 bg-red-50 p-3 rounded-lg">
                <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            {/* Upload Button */}
            <button
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              className={`w-full mt-6 flex items-center justify-center gap-3 py-4 px-6 rounded-xl font-semibold text-white transition-all duration-200 ${
                !selectedFile || uploading
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl'
              }`}
            >
              {uploading ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>{t.receipt.uploading}</span>
                </>
              ) : (
                <>
                  <CloudArrowUpIcon className="w-6 h-6" />
                  <span>{t.receipt.uploadButton}</span>
                </>
              )}
            </button>

            {/* Security Note */}
            <p className="mt-4 text-xs text-center text-gray-500">
              {t.receipt.securityNote}
            </p>
          </div>
        </div>
        </div>
      </div>
    </MainLayout>
  );
}
